import { assert } from "@faro-lotv/foundation";
import {
  Box2,
  Box3,
  DoubleSide,
  Matrix4,
  Mesh,
  OrthographicCamera,
  Quaternion,
  Scene,
  Vector2,
  Vector3,
} from "three";
import { computeSheetSizeAndHeightAdjustment } from "@faro-lotv/app-component-toolbox";
import {
  FloorPlanVisibleTilesStrategy,
  LodFloorPlan,
  LotvRenderer,
} from "@faro-lotv/lotv";
import type { SheetObject } from "@/object-cache";
import { isBackwardCompatibleImgTiledSheet } from "@/components/r3f/renderers/sheet-renderer";
import { SURFACE_REPORT_IMAGE_SIZE } from "./surface-report-camera";
import type { SurfaceSelection } from "./surface-selection-pass";

const MAP_PADDING = 1.1;
const MIN_MAP_EXTENT = 10;
export const SURFACE_REPORT_MAP_TIMEOUT = 45_000;
const MAP_FRAME_DELAY = 16;
const TILED_CENTER_FACTOR = 1.5;
const MARKER = {
  radius: 26,
  fontSize: 56,
  padding: 18,
  offset: 48,
  rise: 94,
  bottomMargin: 88,
  height: 76,
  baseline: 54,
};

/**
 * @param mapBounds World bounds of the overview maps.
 * @param rotation The same top-down orientation used by 2D overview/minimap.
 * @param selection Frozen 3D selection, projected along the map's viewing axis.
 * @returns Independent map camera and pixel-space selection outline and center.
 */
export function surfaceReportMapFrame(
  mapBounds: Box3,
  rotation: Quaternion,
  selection: Pick<SurfaceSelection, "origin" | "positions" | "outline">,
): { camera: OrthographicCamera; outline: Vector2[]; center: Vector2 } {
  assert(selection.positions.length, "The selection has no map location");
  const worldToMap = new Matrix4().makeRotationFromQuaternion(
    rotation.clone().invert(),
  );
  const mapToWorld = new Matrix4().makeRotationFromQuaternion(rotation);
  const bounds = mapBounds.clone().applyMatrix4(worldToMap);
  const point = new Vector3();
  for (let offset = 0; offset < selection.positions.length; offset += 3) {
    point
      .fromArray(selection.positions, offset)
      .add(selection.origin)
      .applyMatrix4(worldToMap);
    bounds.expandByPoint(point);
  }
  const target = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const aspect =
    SURFACE_REPORT_IMAGE_SIZE.width / SURFACE_REPORT_IMAGE_SIZE.height;
  const width = Math.max(MIN_MAP_EXTENT, size.x, size.y * aspect) * MAP_PADDING;
  const height = width / aspect;
  const depth = Math.max(MIN_MAP_EXTENT, size.z + 2);
  const camera = new OrthographicCamera(
    -width / 2,
    width / 2,
    height / 2,
    -height / 2,
    0,
    depth * 2,
  );
  camera.quaternion.copy(rotation);
  camera.up.set(0, 1, 0).applyQuaternion(rotation);
  camera.position
    .copy(target)
    .add(new Vector3(0, 0, depth))
    .applyMatrix4(mapToWorld);
  camera.updateMatrixWorld(true);
  const projected = new Box2();
  const vertices = selection.outline.length
    ? selection.outline
    : selection.positions;
  const outline: Vector2[] = [];
  for (let offset = 0; offset < vertices.length; offset += 3) {
    point.fromArray(vertices, offset).add(selection.origin).project(camera);
    const pixel = new Vector2(
      ((point.x + 1) * SURFACE_REPORT_IMAGE_SIZE.width) / 2,
      ((1 - point.y) * SURFACE_REPORT_IMAGE_SIZE.height) / 2,
    );
    outline.push(pixel);
    projected.expandByPoint(pixel);
  }
  return { camera, outline, center: projected.getCenter(new Vector2()) };
}

/**
 * @param context Map image canvas.
 * @param frame Projected selection, independent of its elevation above the map.
 * @param label Selection label matching the report entry.
 */
export function drawSurfaceReportMapMarker(
  context: CanvasRenderingContext2D,
  frame: Pick<ReturnType<typeof surfaceReportMapFrame>, "outline" | "center">,
  label: string,
): void {
  context.save();
  context.lineJoin = "round";
  context.lineCap = "round";
  for (const stroke of [
    { color: "#ffffff", width: 9 },
    { color: "#ca0033", width: 5 },
  ]) {
    context.strokeStyle = stroke.color;
    context.lineWidth = stroke.width;
    context.beginPath();
    for (let index = 0; index + 1 < frame.outline.length; index += 2) {
      context.moveTo(frame.outline[index].x, frame.outline[index].y);
      context.lineTo(frame.outline[index + 1].x, frame.outline[index + 1].y);
    }
    context.stroke();
  }
  context.beginPath();
  context.arc(frame.center.x, frame.center.y, MARKER.radius, 0, Math.PI * 2);
  context.fillStyle = "#ca0033";
  context.strokeStyle = "white";
  context.lineWidth = 4;
  context.fill();
  context.stroke();
  context.font = `bold ${MARKER.fontSize}px Helvetica, sans-serif`;
  const { padding } = MARKER;
  const labelWidth = context.measureText(label).width + padding * 2;
  const left = Math.max(
    padding,
    Math.min(
      context.canvas.width - labelWidth - padding,
      frame.center.x + MARKER.offset,
    ),
  );
  const top = Math.max(
    padding,
    Math.min(
      context.canvas.height - MARKER.bottomMargin,
      frame.center.y - MARKER.rise,
    ),
  );
  context.fillStyle = "white";
  context.fillRect(left, top, labelWidth, MARKER.height);
  context.fillStyle = "#980028";
  context.fillText(label, left + padding, top + MARKER.baseline);
  context.restore();
}

/**
 * @param image Rendered top-down map image.
 * @param frame Projected selection location.
 * @param label Report entry name.
 * @returns Map image with a clear location overlay, even for edge-on geometry.
 */
export function surfaceReportMapImage(
  image: HTMLCanvasElement,
  frame: ReturnType<typeof surfaceReportMapFrame>,
  label: string,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = SURFACE_REPORT_IMAGE_SIZE.width;
  canvas.height = SURFACE_REPORT_IMAGE_SIZE.height;
  const context = canvas.getContext("2d");
  assert(context, "Map image canvas is unavailable");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  drawSurfaceReportMapMarker(context, frame, label);
  return canvas.toDataURL("image/png");
}

/**
 * @param pending Cached map-loading promises.
 * @param signal Report cancellation, including its map-loading timeout.
 * @returns Loaded maps without cancelling another consumer's shared downloads.
 */
export function loadSurfaceReportMaps(
  pending: Array<Promise<SheetObject> | undefined>,
  signal: AbortSignal,
): Promise<SheetObject[]> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    function abort(): void {
      reject(signal.reason);
    }
    signal.addEventListener("abort", abort, { once: true });
    Promise.all(pending)
      .then((maps) => {
        signal.throwIfAborted();
        assert(
          maps.every((map) => !!map),
          "An overview map could not be loaded",
        );
        resolve(maps);
      })
      .catch(reject)
      .finally(() => signal.removeEventListener("abort", abort));
  });
}

/** Report-owned clones of the active overview maps, with no live scene mutations. */
export class SurfaceReportMapCapture {
  private readonly renderer = new LotvRenderer({
    alpha: false,
    preserveDrawingBuffer: true,
  });
  private readonly scene = new Scene();
  private readonly views: Array<{ object: SheetObject; dispose(): void }> = [];
  private readonly bounds = new Box3();

  /** @param maps Cached maps and their frozen project world transforms. */
  constructor(maps: Array<{ sheet: SheetObject; transform: Matrix4 }>) {
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(
      SURFACE_REPORT_IMAGE_SIZE.width,
      SURFACE_REPORT_IMAGE_SIZE.height,
      false,
    );
    this.renderer.setClearColor("white", 1);
    try {
      for (const { sheet, transform } of maps) {
        let object: SheetObject;
        if (sheet instanceof LodFloorPlan) {
          const view = Object.assign(sheet.createView(), {
            iElement: sheet.iElement,
          });
          if (
            view.visibleTilesStrategy instanceof FloorPlanVisibleTilesStrategy
          ) {
            view.visibleTilesStrategy.minPixelSize =
              sheet.iElement.pixelWidth / 2;
          }
          const size = computeSheetSizeAndHeightAdjustment(
            view,
            isBackwardCompatibleImgTiledSheet(sheet.iElement),
          );
          view.scale.set(size.sheetWidth, size.sheetHeight, 1);
          view.position.set(
            size.sheetWidth / 2,
            size.sheetHeight *
              (TILED_CENTER_FACTOR - size.heightAdjustmentInPercentage),
            0,
          );
          object = view;
          this.views.push({ object, dispose: () => view.dispose() });
        } else {
          const mesh = Object.assign(
            new Mesh(sheet.geometry.clone(), sheet.material.clone()),
            { iElement: sheet.iElement },
          );
          mesh.position.set(
            (sheet.iElement.size?.x ?? 1) / 2,
            (sheet.iElement.size?.z ?? 1) / 2,
            0,
          );
          object = mesh;
          this.views.push({
            object,
            dispose: () => {
              mesh.geometry.dispose();
              mesh.material.dispose();
            },
          });
        }
        object.updateMatrix();
        object.matrix.premultiply(transform);
        object.matrixAutoUpdate = false;
        object.material.opacity = 1;
        object.material.transparent = true;
        object.material.side = DoubleSide;
        object.material.backgroundTransparent =
          sheet.material.backgroundTransparent;
        object.material.altitudeOffset = sheet.material.altitudeOffset;
        object.renderOrder = this.views.length;
        this.scene.add(object);
        object.updateWorldMatrix(true, false);
        this.bounds.union(new Box3().setFromObject(object));
      }
    } catch (error) {
      this.dispose();
      throw error;
    }
  }

  /**
   * @param selection Frozen 3D selection to locate on the map.
   * @param rotation Existing 2D overview orientation.
   * @param label Entry name matching the report.
   * @param signal Cancellation of report generation.
   * @returns Top-down map image with the selection projected onto it.
   */
  async capture(
    selection: SurfaceSelection,
    rotation: Quaternion,
    label: string,
    signal: AbortSignal,
  ): Promise<string> {
    const frame = surfaceReportMapFrame(this.bounds, rotation, selection);
    const started = performance.now();
    const size = new Vector2(
      SURFACE_REPORT_IMAGE_SIZE.width,
      SURFACE_REPORT_IMAGE_SIZE.height,
    );
    let stable = 0;
    while (stable < 2) {
      signal.throwIfAborted();
      if (performance.now() - started > SURFACE_REPORT_MAP_TIMEOUT) {
        throw new Error(
          "Overview-map tiles could not be loaded in time. Please retry the report.",
        );
      }
      let ready = true;
      for (const { object } of this.views) {
        if (object instanceof LodFloorPlan) {
          object.updateVisibleNodes(frame.camera, size);
          ready =
            ready &&
            object.visibleNodesCount > 0 &&
            object.renderedNodesCount >= object.visibleNodesCount;
        }
      }
      stable = ready ? stable + 1 : 0;
      if (stable < 2) {
        await new Promise<void>((resolve) =>
          setTimeout(resolve, MAP_FRAME_DELAY),
        );
      }
    }
    this.renderer.render(this.scene, frame.camera);
    return surfaceReportMapImage(this.renderer.domElement, frame, label);
  }

  /** Release only report-owned geometry, materials, map views and renderer. */
  dispose(): void {
    for (const view of this.views) view.dispose();
    this.views.length = 0;
    this.renderer.dispose();
    this.renderer.forceContextLoss();
  }
}
